import React from 'react';
import { ArrowLeft, Instagram, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 14: STUDIO - Photography/Portfolio, Visual-First (Inspired by Shopify's Studio theme)
const Template14 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-black min-h-screen text-white">
            {/* Minimal Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm">
                <div className="px-8 py-6 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} /> Back
                    </button>

                    <h1 className="text-sm tracking-[0.3em] font-light">STUDIO</h1>

                    <button className="text-sm text-gray-400 hover:text-white transition-colors tracking-wider">
                        MENU
                    </button>
                </div>
            </header>

            {/* Full-Screen Video/Image Hero */}
            <section className="relative h-screen">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1920&h=1080&fit=crop"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black"></div>
                </div>
                <div className="relative h-full flex flex-col justify-end pb-20 px-8">
                    <div className="max-w-4xl">
                        <p className="text-xs tracking-[0.3em] text-gray-300 mb-4">2025 COLLECTION</p>
                        <h2 className="text-6xl md:text-8xl font-light mb-6 leading-none">
                            Visual<br />Storytelling
                        </h2>
                        <p className="text-xl text-gray-300 font-light max-w-2xl">
                            Where fashion meets art. Explore a curated collection designed for those who see beyond the ordinary.
                        </p>
                    </div>
                </div>
            </section>

            {/* Masonry Grid Layout */}
            <section className="px-4 py-20">
                <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                    {[
                        { height: 'h-96', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=800&fit=crop' },
                        { height: 'h-64', image: 'https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=600&h=400&fit=crop' },
                        { height: 'h-80', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=600&fit=crop' },
                        { height: 'h-96', image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&h=800&fit=crop' },
                        { height: 'h-72', image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=600&h=500&fit=crop' },
                        { height: 'h-64', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600&h=400&fit=crop' },
                        { height: 'h-96', image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=600&h=800&fit=crop' },
                        { height: 'h-80', image: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=600&h=600&fit=crop' }
                    ].map((item, index) => (
                        <div key={index} className="break-inside-avoid group cursor-pointer">
                            <div className={`relative ${item.height} overflow-hidden bg-gray-900`}>
                                <img
                                    src={item.image}
                                    alt={`Gallery ${index + 1}`}
                                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:opacity-75"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Play size={48} className="text-white" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured Collection - Horizontal Scroll */}
            <section className="py-20 border-t border-gray-800">
                <div className="px-8 mb-12">
                    <h2 className="text-4xl md:text-5xl font-light mb-4">Latest Drops</h2>
                    <p className="text-gray-400">Scroll to explore</p>
                </div>

                <div className="flex overflow-x-auto space-x-6 px-8 pb-4 scrollbar-hide">
                    {[1, 2, 3, 4, 5, 6].map((id) => (
                        <div key={id} className="flex-none w-72 md:w-96 group cursor-pointer">
                            <div className="relative aspect-[3/4] overflow-hidden bg-gray-900 mb-4">
                                <img
                                    src={`https://images.unsplash.com/photo-${1523381210376 + id * 60000}-7cbb3fc41fea?w=800&h=1000&fit=crop`}
                                    alt={`Product ${id}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                            <h3 className="text-lg font-light mb-2">Collection Item {id}</h3>
                            <p className="text-sm text-gray-400">$XXX.00</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Full-Width Statement */}
            <section className="relative h-screen">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1483118714900-540cf339fd46?w=1920&h=1080&fit=crop"
                        alt="Statement"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>
                <div className="relative h-full flex items-center justify-center px-8 text-center">
                    <div className="max-w-3xl">
                        <h2 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
                            Designed for<br />The Bold
                        </h2>
                        <p className="text-xl text-gray-300 font-light mb-12">
                            Every piece is a canvas. Every outfit tells a story.
                        </p>
                        <button className="px-12 py-4 border border-white text-white hover:bg-white hover:text-black transition-all font-light tracking-wider">
                            EXPLORE NOW
                        </button>
                    </div>
                </div>
            </section>

            {/* Grid Products */}
            <section className="px-8 py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-light mb-4">Shop The Edit</h2>
                        <p className="text-gray-400">Curated selections</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((id) => (
                            <div key={id} className="group cursor-pointer">
                                <div className="relative aspect-square overflow-hidden bg-gray-900 mb-4">
                                    <img
                                        src={`https://images.unsplash.com/photo-${1515886657613 + id * 40000}-9f3515b0c78f?w=600&h=600&fit=crop`}
                                        alt={`Product ${id}`}
                                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:opacity-80"
                                    />
                                    <div className="absolute bottom-4 left-0 right-0 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button className="px-6 py-2 bg-white text-black text-sm font-light tracking-wider">
                                            QUICK VIEW
                                        </button>
                                    </div>
                                </div>
                                <h3 className="text-sm font-light mb-1">Item {id}</h3>
                                <p className="text-sm text-gray-400">$XX.00</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Instagram Feed */}
            <section className="border-t border-gray-800 py-20">
                <div className="px-8 mb-12 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <Instagram size={24} />
                        <h2 className="text-2xl font-light tracking-wider">@studio</h2>
                    </div>
                    <p className="text-gray-400">Follow our journey</p>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 px-2">
                    {[1, 2, 3, 4, 5, 6].map((id) => (
                        <div key={id} className="aspect-square overflow-hidden group cursor-pointer">
                            <img
                                src={`https://images.unsplash.com/photo-${1540518614846 + id * 25000}-e0aa7f38f5e9?w=400&h=400&fit=crop`}
                                alt={`Instagram ${id}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* Newsletter */}
            <section className="border-t border-gray-800 py-20">
                <div className="max-w-2xl mx-auto px-8 text-center">
                    <h3 className="text-3xl font-light mb-4">Join The Studio</h3>
                    <p className="text-gray-400 mb-8">Get exclusive access to new releases and behind-the-scenes content</p>
                    <div className="flex gap-4">
                        <input
                            type="email"
                            placeholder="Email address"
                            className="flex-1 px-6 py-4 bg-transparent border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors"
                        />
                        <button className="px-8 py-4 bg-white text-black font-light tracking-wider hover:bg-gray-200 transition-colors">
                            SUBSCRIBE
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-800 py-16">
                <div className="max-w-7xl mx-auto px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                        <div className="md:col-span-2">
                            <h3 className="text-2xl font-light mb-4 tracking-wider">STUDIO</h3>
                            <p className="text-gray-400 max-w-md">
                                A visual-first fashion experience. Where every frame tells a story and every piece is a work of art.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-sm tracking-wider mb-4 text-gray-300">SHOP</h4>
                            <ul className="space-y-3 text-sm text-gray-500">
                                <li><a href="#" className="hover:text-white transition-colors">New In</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Collections</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Sale</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-sm tracking-wider mb-4 text-gray-300">CONNECT</h4>
                            <ul className="space-y-3 text-sm text-gray-500">
                                <li><a href="#" className="hover:text-white transition-colors">Instagram</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Pinterest</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-600">
                        <p>&copy; 2025 Studio. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template14;
